import React, { useCallback, useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../hooks/useScollToTop";

import { useUser } from "../Home/UserContext";
import { useNotification } from "../../components/Notification/NotificationManager";

import {
  getExpenseProfileByUser,
  upsertExpenseProfileByUser,
  createExpenseClaim,
  updateExpenseClaim,
  computeExpenseDrivingDistance,
  getExpenseClaimsByUser,
} from "../../services/expenseServices";

import HeroHeader from "./components/HeroHeader";
import QuickNav from "./components/QuickNav";
import ExpenseProfileCard from "./components/ExpenseProfileCard";
import EventSelectCard from "./components/EventSelectCard";
import ExpenseMetaCard from "./components/ExpenseMetaCard";
import IndemnitiesCard from "./components/IndemnitiesCard";
import ExpenseItemsCard from "./components/ExpenseItemsCard";
import ExpenseAttachmentsCard from "./components/ExpenseAttachmentsCard";
import ClaimSummaryCard from "./components/ClaimSummaryCard";
import UserExpenseClaimsHistoryCard from "./components/UserExpenseClaimsHistoryCard";

const EMPTY_PROFILE = {
  nom: "",
  prenom: "",
  adresse: "",
  cp: "",
  ville: "",
  fonction: "",
  telephone: "",
  email: "",
};

const toStr = (v) => (v == null ? "" : String(v));
const nil = (v) =>
  typeof v === "string" ? (v.trim() === "" ? null : v.trim()) : v;

const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export default function FicheFraisPage() {
  useScrollToTop();
  const { addNotification } = useNotification();
  const { user } = useUser();

  const stored = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo") || "null");
    } catch {
      return null;
    }
  }, []);

  const [activeTab, setActiveTab] = useState("form");

  // ✅ STATE aligné SUR LES NOMS DE TA TABLE expense_claim_db
  const [expenseDraft, setExpenseDraft] = useState({
    event_mode: "calendar",

    event_id: null,
    titre_event: "",
    start_event: null,
    lieu_event: "",

    code_budgetaire_id: null,

    trajet_necessaire: false,
    moyen_transport: null,
    aller_retour: true,
    depart: "",
    arrivee: "",
    distance_km: "",

    /* NOUVEAU : CALCUL KILOMETRIQUE DETAILLE */
    mileage_mode: "detailed",
    mileage_outbound_stops: [],
    mileage_inbound_stops: [],
    mileage_return_passengers: 0,
    mileage_result: null,

    covoiturage: false,
    covoiturage_role: "",
    covoiturage_nb_passagers: 0,
    covoiturage_avec: "",

    indemnite_tenue: "0",
    indemnite_representation: "0",
    commentaire: "",
  });

  const uid = user?.userId || user?.id || stored?.userId || stored?.id;
  const token = localStorage.getItem("token");

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState(EMPTY_PROFILE);

  const [claimId, setClaimId] = useState(null);
  const [savingClaim, setSavingClaim] = useState(false);
  const [itemsForAttach, setItemsForAttach] = useState([]);

  const [computingDistance, setComputingDistance] = useState(false);

  const [myClaims, setMyClaims] = useState([]);
  const [loadingMyClaims, setLoadingMyClaims] = useState(false);

  const hasValidExpenseEvent = useMemo(() => {
    const hasTitle = !!String(expenseDraft.titre_event || "").trim();
    const hasDate = !!expenseDraft.start_event;
    const hasPlace = !!String(expenseDraft.lieu_event || "").trim();

    if (expenseDraft.event_id) {
      return hasTitle && hasDate && hasPlace;
    }

    return hasTitle && hasDate && hasPlace;
  }, [
    expenseDraft.event_id,
    expenseDraft.titre_event,
    expenseDraft.start_event,
    expenseDraft.lieu_event,
  ]);

  const expenseEventContext = useMemo(() => {
    if (!hasValidExpenseEvent) return null;

    return {
      event_id: expenseDraft.event_id,
      titre: expenseDraft.titre_event,
      start: expenseDraft.start_event,
      lieu_event: expenseDraft.lieu_event,
      source: expenseDraft.event_id ? "calendar" : "manual",
    };
  }, [
    hasValidExpenseEvent,
    expenseDraft.event_id,
    expenseDraft.titre_event,
    expenseDraft.start_event,
    expenseDraft.lieu_event,
  ]);

  const handleExpenseEventChange = (nextEvent) => {
    if (nextEvent.resetClaim) {
      setClaimId(null);
    }

    setExpenseDraft((previousDraft) => {
      const previousPlace = String(previousDraft.lieu_event || "").trim();

      const currentArrival = String(previousDraft.arrivee || "").trim();

      const shouldSynchronizeArrival =
        !currentArrival || currentArrival === previousPlace;

      return {
        ...previousDraft,

        mileage_result: null,

        event_mode: nextEvent.mode === "manual" ? "manual" : "calendar",

        event_id: nextEvent.event_id ?? null,
        titre_event: nextEvent.titre || "",
        start_event: nextEvent.start || null,
        lieu_event: nextEvent.lieu_event || "",

        arrivee: shouldSynchronizeArrival
          ? nextEvent.lieu_event || ""
          : previousDraft.arrivee,
      };
    });
  };

  const buildResidenceAddress = () => {
    const enteredDeparture = String(expenseDraft.depart || "").trim();

    if (enteredDeparture) {
      return enteredDeparture;
    }

    return [draft.adresse, `${draft.cp || ""} ${draft.ville || ""}`.trim()]
      .filter(Boolean)
      .join(", ")
      .trim();
  };

  const buildEventAddress = () =>
    [expenseDraft.arrivee || expenseDraft.lieu_event]
      .filter(Boolean)
      .join(", ")
      .trim();

  const handleComputeDistance = async () => {
    const originAddress = buildResidenceAddress();
    const destinationAddress = buildEventAddress();

    if (!originAddress || !destinationAddress) {
      addNotification("Adresse de départ ou d’arrivée incomplète.", "error");
      return;
    }

    try {
      setComputingDistance(true);

      const result = await computeExpenseDrivingDistance({
        originAddress,
        destinationAddress,
      });

      const distanceAller = Number(result?.distance_km);

      if (!Number.isFinite(distanceAller)) {
        throw new Error("La distance retournée est invalide.");
      }

      setExpenseDraft((previousDraft) => ({
        ...previousDraft,
        depart: previousDraft.depart || originAddress,
        arrivee:
          previousDraft.arrivee ||
          previousDraft.lieu_event ||
          destinationAddress,
        distance_km: String(distanceAller),
      }));

      addNotification(
        `Distance aller calculée : ${distanceAller} km (${result.duration_text || "durée indisponible"})`,
        "success",
      );
    } catch (error) {
      console.error(error);

      addNotification(
        error?.response?.data?.message ||
          error?.message ||
          "Impossible de calculer la distance.",
        "error",
      );
    } finally {
      setComputingDistance(false);
    }
  };

  const prefillFromUser = () => {
    const baseNom = user?.nom || stored?.nom || "";
    const basePrenom = user?.prenom || stored?.prenom || "";
    const baseEmail = user?.email || stored?.email || "";

    setDraft((p) => ({
      ...p,
      nom: p.nom || baseNom,
      prenom: p.prenom || basePrenom,
      email: p.email || baseEmail,
    }));
  };

  const loadProfile = async () => {
    if (!uid || !token) {
      setLoadingProfile(false);
      return;
    }
    try {
      setLoadingProfile(true);
      const data = await getExpenseProfileByUser(uid);
      setProfile(data || null);

      if (data) {
        setDraft({
          nom: toStr(data.nom),
          prenom: toStr(data.prenom),
          adresse: toStr(data.adresse),
          cp: toStr(data.cp),
          ville: toStr(data.ville),
          fonction: toStr(data.fonction),
          telephone: toStr(data.telephone),
          email: toStr(data.email),
        });
      } else {
        setDraft(EMPTY_PROFILE);
        prefillFromUser();
      }
    } catch (e) {
      console.error(e);
      addNotification(
        "Impossible de charger vos coordonnées (profil frais).",
        "error",
      );
      setProfile(null);
      setDraft(EMPTY_PROFILE);
      prefillFromUser();
    } finally {
      setLoadingProfile(false);
    }
  };

  const loadMyClaims = useCallback(async () => {
    if (!uid || !token) {
      setMyClaims([]);
      setLoadingMyClaims(false);
      return;
    }

    try {
      setLoadingMyClaims(true);
      const data = await getExpenseClaimsByUser(uid);
      setMyClaims(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      addNotification("Impossible de charger vos fiches de frais.", "error");
      setMyClaims([]);
    } finally {
      setLoadingMyClaims(false);
    }
  }, [uid, token, addNotification]);

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => {
    loadMyClaims();
  }, [loadMyClaims]);

  const handleSaveProfile = async () => {
    if (!uid || !token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }

    const required = ["nom", "prenom", "adresse", "cp", "ville"];
    for (const f of required) {
      if (!String(draft[f] || "").trim()) {
        addNotification(`Champ requis : ${f}`, "error");
        return;
      }
    }

    try {
      setSavingProfile(true);
      const saved = await upsertExpenseProfileByUser(uid, {
        nom: nil(draft.nom),
        prenom: nil(draft.prenom),
        adresse: nil(draft.adresse),
        cp: nil(draft.cp),
        ville: nil(draft.ville),
        fonction: nil(draft.fonction),
        telephone: nil(draft.telephone),
        email: nil(draft.email),
      });
      setProfile(saved);
      addNotification("Coordonnées enregistrées ✅", "success");
    } catch (e) {
      console.error(e);
      addNotification(
        "Erreur lors de l’enregistrement des coordonnées.",
        "error",
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const isConnected = !!uid && !!token;

  const buildClaimPayload = () => {
    const d = expenseDraft;
    /* CALCUL DETAILLE */

    const isDetailedMileage =
      d.trajet_necessaire &&
      d.moyen_transport === "voiture" &&
      d.mileage_mode === "detailed" &&
      (!d.covoiturage || d.covoiturage_role === "Conducteur");

    const mileageDetail = isDetailedMileage
      ? {
          roundTrip: !!d.aller_retour,

          outbound: {
            start: String(d.depart || "").trim(),
            end: String(d.arrivee || "").trim(),
            initialPassengers: 0,
            stops: d.mileage_outbound_stops || [],
          },

          inbound: d.aller_retour
            ? {
                start: String(d.arrivee || "").trim(),
                end: String(d.depart || "").trim(),
                initialPassengers: Number(d.mileage_return_passengers ?? 0),
                stops: d.mileage_inbound_stops || [],
              }
            : null,
        }
      : null;

    const trajet = !!d.trajet_necessaire;
    const moyen = trajet ? d.moyen_transport || "voiture" : null;

    const cov = moyen === "voiture" ? !!d.covoiturage : false;

    const role = cov ? String(d.covoiturage_role || "").trim() : null;
    const avec = cov ? String(d.covoiturage_avec || "").trim() : null;

    const allStops = [
      ...(d.mileage_outbound_stops || []),
      ...(d.aller_retour ? d.mileage_inbound_stops || [] : []),
    ];

    const passengerNames = [
      ...new Set(
        allStops
          .flatMap((stop) =>
            String(stop.names || "")
              .split(",")
              .map((name) => name.trim()),
          )
          .filter(Boolean),
      ),
    ];

    const calculatedLegs = [
      ...(d.mileage_result?.aller?.legs || []),
      ...(d.mileage_result?.retour?.legs || []),
    ];

    const maxPassengers = Math.max(
      0,
      Number(d.mileage_return_passengers || 0),
      ...calculatedLegs.map((leg) => Number(leg.passengers || 0)),
    );

    return {
      user_id: uid,
      event_id: numOrNull(d.event_id),
      event_mode: d.event_mode === "manual" ? "manual" : "calendar",
      titre_event: nil(d.titre_event),
      start_event: d.start_event || null,
      lieu_event: nil(d.lieu_event),
      code_budgetaire_id: numOrNull(d.code_budgetaire_id),

      statut: "draft",

      trajet_necessaire: trajet,
      moyen_transport: moyen,
      aller_retour: trajet ? !!d.aller_retour : true,
      depart: trajet ? String(d.depart || "").trim() || null : null,
      arrivee: trajet ? String(d.arrivee || "").trim() || null : null,
      distance_km: trajet
        ? isDetailedMileage
          ? numOrNull(d.mileage_result?.distance_km)
          : numOrNull(d.distance_km)
        : null,

      mileage_mode: isDetailedMileage ? "detailed" : "legacy",

      mileage_detail: mileageDetail,

      covoiturage: isDetailedMileage ? maxPassengers > 0 : cov,

      covoiturage_role: isDetailedMileage
        ? maxPassengers > 0
          ? "Conducteur"
          : null
        : cov
          ? role
          : null,

      covoiturage_nb_passagers: isDetailedMileage
        ? maxPassengers
        : cov && role === "Conducteur"
          ? Math.max(0, Math.floor(Number(d.covoiturage_nb_passagers ?? 0)))
          : 0,

      covoiturage_avec: isDetailedMileage
        ? maxPassengers > 0
          ? passengerNames.join(", ") || "Passagers détaillés dans l'itinéraire"
          : null
        : cov
          ? avec
          : null,

      indemnite_tenue: numOrNull(d.indemnite_tenue) ?? 0,
      indemnite_representation: numOrNull(d.indemnite_representation) ?? 0,

      commentaire: (d.commentaire || "").trim() || null,
    };
  };

  const saveClaim = async () => {
    if (!uid || !token) {
      addNotification("Session expirée, veuillez vous reconnecter.", "error");
      return;
    }
    if (!hasValidExpenseEvent) {
      addNotification(
        "Sélectionnez un événement du calendrier ou renseignez le nom, la date et le lieu.",
        "error",
      );
      return;
    }

    /* ========================================================================
   VALIDATION DU CALCUL KILOMETRIQUE DETAILLE
   ======================================================================== */

    const isDetailedMileage =
      expenseDraft.trajet_necessaire &&
      expenseDraft.moyen_transport === "voiture" &&
      expenseDraft.mileage_mode === "detailed" &&
      (!expenseDraft.covoiturage ||
        expenseDraft.covoiturage_role === "Conducteur");

    if (isDetailedMileage && !expenseDraft.mileage_result) {
      addNotification(
        "Veuillez calculer les kilomètres et indemnités avant d'enregistrer.",
        "error",
      );
      return;
    }

    try {
      const payload = buildClaimPayload();

      if (payload.covoiturage) {
        if (
          payload.covoiturage_role !== "Conducteur" &&
          payload.covoiturage_role !== "Passager"
        ) {
          addNotification(
            "Choisis un rôle covoiturage : Conducteur ou Passager.",
            "error",
          );
          return;
        }
        if (!payload.covoiturage_avec) {
          addNotification("Indique avec qui tu covoitures (nom(s)).", "error");
          return;
        }
      }

      setSavingClaim(true);

      const saved = claimId
        ? await updateExpenseClaim(claimId, payload)
        : await createExpenseClaim(payload);

      if (saved?.id) {
        setClaimId(saved.id);
      }

      if (isDetailedMileage) {
        const serverResult = saved?.mileage_detail?.result;

        if (!serverResult) {
          throw new Error("Le serveur n'a pas retourné le calcul détaillé.");
        }

        setExpenseDraft((previous) => ({
          ...previous,
          distance_km: String(serverResult.distance_km),
          mileage_result: serverResult,
        }));
      }

      addNotification("Fiche enregistrée ✅", "success");
      await loadMyClaims();
    } catch (e) {
      console.error(e?.response?.data || e);
      addNotification(
        e?.response?.data?.message ||
          e?.message ||
          "Erreur lors de l'enregistrement de la fiche.",
        "error",
      );
    } finally {
      setSavingClaim(false);
    }
  };

  const tabButtonClass = (active) =>
    [
      "inline-flex items-center rounded-full px-4 py-2.5 text-sm font-semibold transition",
      active
        ? "bg-slate-900 text-white shadow-sm"
        : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50",
    ].join(" ");

  return (
    <div className="w-full pt-[70px] font-satoshi">
      <HeroHeader />

      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
        <div className="relative text-white px-4 py-4 md:py-5 max-w-[1200px] mx-auto">
          <p className="text-xs md:text-sm opacity-90">
            <a href="/" className="hover:underline">
              Accueil
            </a>{" "}
            &gt; Fiche de frais
          </p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 py-6">
        <QuickNav />

        {!isConnected ? (
          <div className="rounded-2xl ring-1 ring-slate-200/70 bg-white/90 shadow-sm p-6">
            <div className="text-lg font-semibold">Connexion requise</div>
            <p className="text-slate-700 mt-2">
              Vous devez être connecté pour accéder à la fiche de frais.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              (uid={String(uid || "null")} / token={token ? "ok" : "absent"})
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6 rounded-2xl ring-1 ring-slate-200/70 bg-white/90 shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className={tabButtonClass(activeTab === "form")}
                  onClick={() => setActiveTab("form")}
                >
                  Nouvelle fiche
                </button>

                <button
                  type="button"
                  className={tabButtonClass(activeTab === "history")}
                  onClick={() => setActiveTab("history")}
                >
                  Mes fiches de frais
                  <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-700">
                    {myClaims.length}
                  </span>
                </button>
              </div>
            </div>

            {activeTab === "form" ? (
              <>
                <ExpenseProfileCard
                  loading={loadingProfile}
                  saving={savingProfile}
                  profile={profile}
                  draft={draft}
                  setDraft={setDraft}
                  onSave={handleSaveProfile}
                  onReload={loadProfile}
                />

                <EventSelectCard
                  modeValue={expenseDraft.event_mode}
                  value={expenseDraft.event_id}
                  titreValue={expenseDraft.titre_event}
                  startValue={expenseDraft.start_event}
                  lieuValue={expenseDraft.lieu_event}
                  onChange={handleExpenseEventChange}
                />

                <ExpenseMetaCard
                  eventSelected={expenseEventContext}
                  draft={expenseDraft}
                  setDraft={setExpenseDraft}
                  onComputeDistance={handleComputeDistance}
                  computingDistance={computingDistance}
                  setComputingDistance={setComputingDistance}
                  residenceAddress={buildResidenceAddress()}
                />

                <IndemnitiesCard
                  draft={expenseDraft}
                  setDraft={setExpenseDraft}
                  saving={savingClaim}
                  onSave={saveClaim}
                  disabled={!hasValidExpenseEvent}
                />

                <ExpenseItemsCard
                  claimId={claimId}
                  onEnsureClaim={saveClaim}
                  onItemsChange={setItemsForAttach}
                  onChanged={loadMyClaims}
                />

                <ExpenseAttachmentsCard
                  claimId={claimId}
                  items={itemsForAttach}
                  onEnsureClaim={saveClaim}
                  onChanged={loadMyClaims}
                />

                <ClaimSummaryCard claimId={claimId} onEnsureClaim={saveClaim} />
              </>
            ) : (
              <UserExpenseClaimsHistoryCard
                claims={myClaims}
                loading={loadingMyClaims}
                onRefresh={loadMyClaims}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
